import type { SubscriptionTier, SubscriptionStatus } from "../../../api/General/Subscription";

/* ─── Day math (calendar-day, not millisecond) ─── */
export function daysBetween(from: Date, to: Date): number {
  const ms = 24 * 60 * 60 * 1000;
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / ms);
}

export function daysFromNow(iso?: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return daysBetween(new Date(), d);
}

/* ─── Humanize an ISO date relative to "now" ─── */
export interface HumanizedDate {
  label: string;
  /** "ok" | "soon" (≤7d) | "expired" | "muted" (no date) */
  tone: "ok" | "soon" | "expired" | "muted";
  /** Raw days delta (+ future, - past, 0 today). null when no date. */
  delta: number | null;
}

export function humanizeRelative(
  iso: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
  fallback = "—",
): HumanizedDate {
  if (!iso) return { label: fallback, tone: "muted", delta: null };
  const delta = daysFromNow(iso);
  if (delta === null) return { label: fallback, tone: "muted", delta: null };
  if (delta === 0) return { label: t("expires today"), tone: "soon", delta: 0 };
  if (delta < 0) {
    return { label: t("expired {{days}}d ago", { days: Math.abs(delta) }), tone: "expired", delta };
  }
  // Future
  const tone: HumanizedDate["tone"] = delta <= 7 ? "soon" : "ok";
  return { label: t("in {{days}}d", { days: delta }), tone, delta };
}

/* ─── Relative timestamp ("just now", "3h ago", "2d ago", "16 May") ─── */
export function relativeTime(
  iso: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
  fallback = "—",
): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  const now = new Date();
  const diffSec = Math.round((now.getTime() - d.getTime()) / 1000);
  if (diffSec < 60) return t("Just now");
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = daysBetween(d, now);
  if (diffDay < 30) return t("{{days}}d", { days: diffDay });
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function formatDate(iso: string | null | undefined, fallback = "—"): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/* ─── Format an HTML <input type="date"> value (YYYY-MM-DD) ─── */
export function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/* ─── Tier / status visual mapping ─── */
export interface TierVisual {
  variant: "neutral" | "warning" | "success";
  pill: string;
  text: string;
}

export function tierVisual(tier: SubscriptionTier): TierVisual {
  switch (tier) {
    case "Trial":
      return {
        variant: "warning",
        pill: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-1 ring-amber-200/60 dark:ring-amber-900/40",
        text: "text-amber-700 dark:text-amber-300",
      };
    case "Paid":
      return {
        variant: "success",
        pill: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-200/60 dark:ring-emerald-900/40",
        text: "text-emerald-700 dark:text-emerald-300",
      };
    case "Free":
    default:
      return {
        variant: "neutral",
        pill: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200/60 dark:ring-slate-700/60",
        text: "text-slate-700 dark:text-slate-300",
      };
  }
}

export type StatusTone = "ok" | "bad";

export function statusTone(status: SubscriptionStatus): StatusTone {
  return status === "Active" ? "ok" : "bad";
}

/* ─── Initials for avatar fallback ─── */
export function initials(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0)).join("").toUpperCase() || "?";
}
